import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Collapse,
  Grid,
  Alert,
  Box,
  useTheme,
} from '@mui/material';
import { ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuthMethod } from 'types';
import { useGoogleLogin, useFacebookLogin } from 'services/hooks';
import { HTTPError } from 'services/api/entities';
import type { AuthStore } from 'services/store/auth/types';
import { locations } from 'services/routes/locations';
import { GoogleLogin, FacebookLogin, displayVariant } from 'components/Login';
import { LoadingButton, TextDivider } from 'components';
import FormContainer from 'pages/FormContainer';
import FormHeader from 'pages/FormContainer/components/FormHeader';
import FormFooter from 'pages/FormContainer/components/FormFooter';
import useValidationSchema from './services/useValidationSchema';

type LoginFormProps = {
  onSuccess: (method: AuthMethod) => void;
  isLoading: boolean;
  loginError?: HTTPError;
  auth: AuthStore;
  loginWithCredentials: (loginParams: {
    email: string;
    password: string;
  }) => void;
};

const LoginForm = ({
  onSuccess,
  isLoading,
  loginError,
  auth,
  loginWithCredentials,
}: LoginFormProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const signInSchema = useValidationSchema();
  const { handleSubmit, register, formState, clearErrors } = useForm({
    resolver: yupResolver(signInSchema),
    mode: 'onBlur',
  });
  const { errors } = formState;
  const { isLoggedIn } = auth;

  const authMethod = useRef<AuthMethod>(AuthMethod.Email);

  const {
    loading: googleLoginLoading,
    error: googleLoginError,
    loginWithGoogle,
  } = useGoogleLogin();

  const {
    loading: facebookLoginLoading,
    error: facebookLoginError,
    loginWithFacebook,
  } = useFacebookLogin();

  useEffect(() => {
    if (isLoggedIn) {
      onSuccess(authMethod.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // @ts-ignore
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // @ts-ignore
  const onSubmit = (data) => {
    loginWithCredentials(data);
  };

  const setAuthMethod = (method: AuthMethod) => {
    authMethod.current = method;
  };

  const hasError = () =>
    !!loginError || !!googleLoginError || !!facebookLoginError;

  const errorMessage = useCallback(() => {
    if (loginError) {
      return loginError.message;
    }
    if (googleLoginError) {
      return googleLoginError.message;
    }
    if (facebookLoginError) {
      return facebookLoginError.message;
    }
    return null;
  }, [facebookLoginError, googleLoginError, loginError]);

  const inputStyle = useMemo(
    () => ({
      marginBottom: theme.spacing(3),
      width: '100%',
      '& input:-webkit-autofill': {
        '-webkit-background-clip': 'text',
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-webkit-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
      '& input:-moz-autofill': {
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-moz-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
      '& input:-ms-autofill': {
        boxShadow: `0 0 0 100px ${theme.palette.grey[900]} inset`,
        '-ms-box-shadow': `0 0 0 100px ${theme.palette.grey[900]} inset`,
      },
    }),
    [theme]
  );

  const errorAlertMessage = useRef<HTMLElement>();

  useEffect(() => {
    if (!errorAlertMessage.current) return;
    window.scrollTo(0, 0);
  }, [errorAlertMessage, errorMessage]);

  return (
    <FormContainer>
      <Box
        sx={{
          maxWidth: theme.spacing(48.75),
          minHeight: '100%',
        }}
      >
        <Collapse in={hasError()} ref={errorAlertMessage}>
          <Alert
            variant="outlined"
            severity="error"
            color="error"
            sx={{
              marginTop: theme.spacing(5),
              [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(6),
                color: theme.palette.grey[50],
                fontSize: 12,
              },
            }}
            data-testid="loginErrorAlert"
            icon={<ErrorOutline color="error" />}
          >
            {hasError() && errorMessage()}
          </Alert>
        </Collapse>

        <FormHeader
          title={t('auth:logIn')}
          description={t('auth:noAccount')}
          linkText={t('auth:register')}
          linkURL={locations.general.register.pathname('')}
        />

        <Box
          component="form"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box>
            <Grid
              mb={2}
              sx={{
                overflow: 'hidden',
                borderRadius: theme.spacing(2.5),
              }}
            >
              <GoogleLogin
                handleSuccess={(token: string) => {
                  setAuthMethod(AuthMethod.Google);
                  loginWithGoogle(token);
                }}
              />
            </Grid>
            <Grid>
              <FacebookLogin
                variant={displayVariant.REGULAR}
                loading={facebookLoginLoading}
                handleSuccess={(token: string) => {
                  setAuthMethod(AuthMethod.Facebook);
                  loginWithFacebook(token);
                }}
              />
            </Grid>
          </Box>

          <TextDivider />

          <TextField
            sx={inputStyle}
            id="email"
            type="email"
            label={t('fields:email')}
            variant="outlined"
            defaultValue=""
            {...register('email', {
              onChange: () => {
                clearErrors('email');
              },
            })}
            error={!!errors.email}
            helperText={errors.email && errors.email.message}
          />

          <FormControl
            variant="outlined"
            sx={inputStyle}
            error={!!errors.password}
          >
            <InputLabel htmlFor="outlined-adornment-password">
              {t('fields:password')}
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                onChange: () => {
                  clearErrors('password');
                },
              })}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    data-testid="show-password"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
              label={t('fields:password')}
            />
            {errors.password && (
              <FormHelperText>{errors.password.message}</FormHelperText>
            )}
          </FormControl>

          <LoadingButton
            sx={{ marginTop: theme.spacing(1) }}
            color="primary"
            variant="rounded"
            fullWidth
            type="submit"
            loading={isLoading}
            disabled={googleLoginLoading || facebookLoginLoading}
            data-testid="submit-button"
            onClick={() => setAuthMethod(AuthMethod.Email)}
          >
            {t('auth:logIn')}
          </LoadingButton>
        </Box>

        <FormFooter link={t('auth:forgotPasswordStatement')} />
      </Box>
    </FormContainer>
  );
};

export default LoginForm;
